import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ErrorService } from '@services/error/error.service';
import { API_ROUTE } from '@constants/api.routes.constants';
import { buildDayLog, DayLog } from '../models/day-log';
import { YearMonth } from '../models/year-month';

@Injectable({
  providedIn: 'root',
})
export class LogsService {
  public constructor(
    private errorService: ErrorService,
    private readonly httpClient: HttpClient
  ) {}

  public getAvailableYearMonths(
    employeeId: string,
    managerId?: string
  ): Observable<YearMonth[]> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json; charset=utf-8',
        Employeeid: employeeId,
      }),
    };
    let url = API_ROUTE.LOGS.EMPLOYEE_AVAILABLE_YEAR_MONTHS;
    if (managerId) {
      url = API_ROUTE.LOGS.EMPLOYEE_OTHER_AVAILABLE_YEAR_MONTHS;
      httpOptions.headers.append('Managerid', managerId);
    }

    return this.httpClient
      .get<any>(url, httpOptions)
      .pipe(
        map((yearMonthsInfo: any[]): YearMonth[] =>
          yearMonthsInfo.map(
            (yearMonthInfo: any): YearMonth =>
              YearMonth.factory(
                yearMonthInfo.year,
                yearMonthInfo.month,
                yearMonthInfo.isMonthConsolidated
              )
          )
        )
      );
  }

  public getYearMonthLogs(
    employeeId: string,
    yearMonth: YearMonth,
    managerId?: string
  ): Observable<DayLog[]> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json; charset=utf-8',
        Employeeid: employeeId,
      }),
    };
    let url = API_ROUTE.LOGS.EMPLOYEE_MONTH_LOGS;
    if (managerId) {
      url = API_ROUTE.LOGS.EMPLOYEE_OTHER_MONTH_LOGS;
      httpOptions.headers.append('Managerid', managerId);
    }

    return this.httpClient
      .get<any>(url, {
        ...httpOptions,
        params: {
          month: yearMonth.month.toString(),
          year: yearMonth.year.toString(),
          systemTime: moment().format('HHmm'),
          systemDate: moment().format('YYYY-MM-DD'),
        },
      })
      .pipe(
        map((rawDayLogs: any[]): DayLog[] => rawDayLogs.map(buildDayLog)),
        catchError((err: Error): Observable<DayLog[]> => {
          this.errorService.set('ERROR_DEFAULT', err);
          return of(null);
        })
      );
  }

  public updateBalanceNegativeByDay(
    compensateDate: string,
    compensateTime: number,
    compensateReason: string,
    employeeId: string
  ) {
    const url = API_ROUTE.LOGS.UPDATE_BALANCE_COMPENSATION;

    const body = {
      compensateDate,
      compensateTime,
      compensateReason,
    };

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json; charset=utf-8',
        Employeeid: employeeId,
      }),
    };

    return this.httpClient.post<any>(url, body, httpOptions);
  }
}
